import type {
  SalesByCategoryReportResult,
  SalesReportColumn,
  SalesReportPaymentMethod,
  SalesReportRow,
} from './types';

import { formatCentsCompact } from '@/shared/lib/money';

export interface SalesReportTableView {
  visible_columns: SalesReportColumn[];
  cell_lookup: Map<string, Map<string, string>>;
  column_totals: Map<string, bigint>;
  row_totals: Map<string, bigint>;
  grand_total: bigint;
}

export interface SalesReportPrintStrings {
  title: string;
  period_label: string;
  ticket_label: string;
  pet_label: string;
  totals_label: string;
  row_total_label: string;
}

const PRINT_DATE_FORMAT = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export function buildSalesReportTableView(
  report: SalesByCategoryReportResult | null,
): SalesReportTableView {
  const columns = report?.columns ?? [];
  const rows = report?.rows ?? [];
  const cell_lookup = new Map<string, Map<string, string>>();
  const column_totals = new Map<string, bigint>();
  const row_totals = new Map<string, bigint>();

  for (const column of columns) column_totals.set(column.column_key, 0n);

  let grand_total = 0n;

  for (const row of rows) {
    const per_row = new Map<string, string>();
    for (const cell of row.cells) {
      per_row.set(cell.column_key, cell.amount_cents);
    }
    cell_lookup.set(row.sale_order_id, per_row);

    let row_total = 0n;
    for (const column of columns) {
      const amount = parseBigInt(per_row.get(column.column_key));
      row_total += amount;
      column_totals.set(column.column_key, (column_totals.get(column.column_key) ?? 0n) + amount);
    }

    row_totals.set(row.sale_order_id, row_total);
    grand_total += row_total;
  }

  const visible_columns = columns.filter(
    (column) => (column_totals.get(column.column_key) ?? 0n) > 0n,
  );

  return {
    visible_columns,
    cell_lookup,
    column_totals,
    row_totals,
    grand_total,
  };
}

export function formatSalesReportPeriodLabel(from_iso: string, to_iso: string): string {
  const from = parseIsoDate(from_iso);
  const to = parseIsoDate(to_iso);
  if (!from || !to) return '';

  const from_label = PRINT_DATE_FORMAT.format(from);
  const to_label = PRINT_DATE_FORMAT.format(to);
  if (from_label === to_label) return from_label;
  return `${from_label} al ${to_label}`;
}

export function buildSalesReportPrintHtml(args: {
  report: SalesByCategoryReportResult | null;
  period_label: string;
  strings: SalesReportPrintStrings;
}): string {
  const report = args.report;
  const view = buildSalesReportTableView(report);
  const rows = sortSalesReportRowsForPrint(report?.rows ?? []);
  const visible_columns = view.visible_columns;
  const category_width =
    visible_columns.length > 0 ? (66 / visible_columns.length).toFixed(2) : '0';

  const total_columns = visible_columns.length + 2;
  const rows_markup: string[] = [];
  let current_section: SalesReportPaymentSortMeta | null = null;
  let current_section_column_totals = createSalesReportAmountMap(visible_columns);
  let current_section_total = 0n;

  const flushCurrentSection = (): void => {
    if (!current_section) return;
    rows_markup.push(
      renderSalesReportSectionTotalsRow({
        meta: current_section,
        visible_columns,
        column_totals: current_section_column_totals,
        section_total: current_section_total,
      }),
    );
  };

  for (const row of rows) {
    const meta = buildSalesReportPaymentSortMeta(row.payment_methods);
    if (!current_section || current_section.section_key !== meta.section_key) {
      flushCurrentSection();
      current_section = meta;
      current_section_column_totals = createSalesReportAmountMap(visible_columns);
      current_section_total = 0n;
      rows_markup.push(`
        <tr class="group-row">
          <td class="group-cell" colspan="${total_columns}">${escapeHtml(meta.section_label)}</td>
        </tr>
      `);
    }

    const row_cells = view.cell_lookup.get(row.sale_order_id) ?? new Map<string, string>();
    const ticket =
      row.ticket_folio_number ?? row.sale_folio_number ?? row.sale_order_id.slice(0, 8);
    const pet = row.pet_name ?? '';
    const ticket_label = escapeHtml(ticket);
    const pet_label = escapeHtml(pet);
    const row_total = view.row_totals.get(row.sale_order_id) ?? 0n;
    const amount_cells = visible_columns
      .map((column) => {
        const amount = row_cells.get(column.column_key) ?? '0';
        const parsed_amount = parseBigInt(amount);
        current_section_column_totals.set(
          column.column_key,
          (current_section_column_totals.get(column.column_key) ?? 0n) + parsed_amount,
        );
        return `<td class="amount">${escapeHtml(formatCentsCompact(amount))}</td>`;
      })
      .join('');

    current_section_total += row_total;

    rows_markup.push(`
      <tr>
        <td class="ticket-cell">
          <div class="ticket-value">${ticket_label}</div>
          <div class="pet-value">${escapeHtml(args.strings.pet_label)}: ${pet_label || '&mdash;'}</div>
        </td>
        ${amount_cells}
        <td class="amount amount--row-total">${escapeHtml(formatCentsCompact(row_total.toString()))}</td>
      </tr>
    `);
  }

  flushCurrentSection();
  const rows_html = rows_markup.join('');

  const totals_cells = visible_columns
    .map((column) => {
      const amount = view.column_totals.get(column.column_key) ?? 0n;
      return `<td class="amount amount--total">${escapeHtml(formatCentsCompact(amount.toString()))}</td>`;
    })
    .join('');

  const header_cells = visible_columns
    .map((column) => `<th class="category-head">${escapeHtml(column.category_name)}</th>`)
    .join('');

  const totals_row_html = `
    <tr>
      <td class="totals-label">
        ${escapeHtml(args.strings.totals_label)}
        <strong>${escapeHtml(args.strings.row_total_label)}</strong>
      </td>
      ${totals_cells}
      <td class="amount amount--total grand-total">${escapeHtml(formatCentsCompact(view.grand_total.toString()))}</td>
    </tr>
  `;

  return `<!doctype html>
<html lang="es">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(args.strings.title)}</title>
    <style>
      :root {
        color-scheme: light;
        --text: #111111;
        --muted: #5f6368;
        --border: #d7dbe0;
        --border-strong: #9ca3af;
        --surface: #ffffff;
        --surface-alt: #f6f7f9;
      }

      @page {
        size: landscape;
        margin: 10mm;
      }

      html, body {
        margin: 0;
        padding: 0;
        background: var(--surface);
        color: var(--text);
        font-family: Arial, Helvetica, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      body {
        padding: 0;
      }

      .report {
        width: 100%;
        box-sizing: border-box;
        padding: 0;
      }

      .header {
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 14px;
        padding-bottom: 10px;
        border-bottom: 2px solid var(--text);
      }

      .title {
        margin: 0;
        font-size: 22px;
        line-height: 1.1;
        font-weight: 700;
        letter-spacing: -0.02em;
      }

      .period {
        margin: 4px 0 0;
        font-size: 13px;
        line-height: 1.35;
        color: var(--muted);
      }

      .period strong {
        color: var(--text);
        font-weight: 700;
      }

      .table-wrap {
        width: 100%;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
      }

      col.ticket-col {
        width: 20%;
      }

      col.total-col {
        width: 14%;
      }

      col.category-col {
        width: ${category_width}%;
      }

      thead th {
        border: 1px solid var(--border-strong);
        background: var(--text);
        color: #ffffff;
        padding: 8px 6px;
        font-size: 11px;
        line-height: 1.2;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-weight: 700;
      }

      tbody td,
      .totals-table td {
        border: 1px solid var(--border);
        vertical-align: top;
        padding: 6px 5px;
        font-size: 11px;
        line-height: 1.2;
      }

      tbody tr:nth-child(even) td {
        background: var(--surface-alt);
      }

      .group-row td {
        background: #dfe6ee;
        border: 1px solid var(--border-strong);
      }

      .group-cell {
        padding: 8px 10px;
        font-size: 12px;
        line-height: 1.2;
        font-weight: 800;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: var(--text);
      }

      .ticket-cell {
        word-break: break-word;
      }

      .ticket-value {
        font-size: 13px;
        font-weight: 700;
        margin-bottom: 4px;
      }

      .pet-value {
        font-size: 11px;
        color: var(--muted);
      }

      .amount {
        text-align: right;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        letter-spacing: -0.01em;
      }

      .amount--row-total {
        font-size: 11px;
        font-weight: 700;
        color: var(--text);
      }

      .subtotal-row td {
        background: #edf3f8;
        border-top: 2px solid var(--border-strong);
        font-weight: 700;
      }

      .subtotal-label {
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 12px;
        color: var(--muted);
      }

      .subtotal-label strong {
        display: block;
        margin-top: 3px;
        color: var(--text);
        text-transform: none;
        letter-spacing: normal;
        font-size: 14px;
      }

      .amount--subtotal {
        font-size: 11px;
        color: var(--text);
      }

      .totals-wrap {
        margin-top: 10px;
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .totals-table td {
        background: #eef2f6;
        font-weight: 700;
      }

      .totals-label {
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 12px;
        color: var(--muted);
      }

      .totals-label strong {
        display: block;
        margin-top: 3px;
        color: var(--text);
        text-transform: none;
        letter-spacing: normal;
        font-size: 14px;
      }

      .amount--total {
        font-size: 11px;
      }

      .grand-total {
        font-size: 12px;
        font-weight: 800;
      }

      .no-data {
        border: 1px solid var(--border);
        background: var(--surface-alt);
        padding: 18px;
        font-size: 14px;
        color: var(--muted);
      }
    </style>
  </head>
  <body>
    <main class="report">
      <header class="header">
        <div>
          <h1 class="title">${escapeHtml(args.strings.title)}</h1>
          <p class="period">${escapeHtml(args.strings.period_label)} <strong>${escapeHtml(args.period_label)}</strong></p>
        </div>
      </header>

      ${
        rows.length === 0
          ? `<div class="no-data">${escapeHtml(args.strings.title)}: sin datos para imprimir.</div>`
          : `
      <section class="table-wrap">
        <table>
          <colgroup>
            <col class="ticket-col" />
            ${visible_columns.map(() => '<col class="category-col" />').join('')}
            <col class="total-col" />
          </colgroup>
          <thead>
            <tr>
              <th>${escapeHtml(args.strings.ticket_label)}</th>
              ${header_cells}
              <th>${escapeHtml(args.strings.row_total_label)}</th>
            </tr>
          </thead>
          <tbody>
            ${rows_html}
          </tbody>
        </table>
      </section>
      <section class="table-wrap totals-wrap">
        <table class="totals-table">
          <colgroup>
            <col class="ticket-col" />
            ${visible_columns.map(() => '<col class="category-col" />').join('')}
            <col class="total-col" />
          </colgroup>
          <tbody>
            ${totals_row_html}
          </tbody>
        </table>
      </section>
      `
      }
    </main>
  </body>
</html>`;
}

type SalesReportPaymentGroup = 'cash' | 'debit_card' | 'credit_card';

interface SalesReportPaymentSortMeta {
  group: SalesReportPaymentGroup;
  priority: number;
  sort_order: number;
  method_name: string;
  section_key: string;
  section_label: string;
}

export function sortSalesReportRowsForPrint(rows: readonly SalesReportRow[]): SalesReportRow[] {
  return [...rows].sort((left, right) => compareSalesReportRowsForPrint(left, right));
}

export function sortSalesReportRowsChronologically(
  rows: readonly SalesReportRow[],
): SalesReportRow[] {
  return [...rows].sort((left, right) => {
    const paid_at_cmp = parseRowDate(left.paid_at) - parseRowDate(right.paid_at);
    if (paid_at_cmp !== 0) return paid_at_cmp;
    return left.sale_order_id.localeCompare(right.sale_order_id, 'en');
  });
}

export function printHtmlInHiddenWindow(html: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '1200px';
    iframe.style.height = '900px';
    iframe.style.border = '0';
    iframe.style.pointerEvents = 'none';

    const cleanup = (): void => {
      iframe.removeEventListener('load', on_load);
      const win = iframe.contentWindow;
      if (win) win.removeEventListener('afterprint', on_after_print);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const on_after_print = (): void => {
      cleanup();
      resolve();
    };

    const on_load = async (): Promise<void> => {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        reject(new Error('No se pudo preparar la vista imprimible.'));
        return;
      }

      win.addEventListener('afterprint', on_after_print, { once: true });
      try {
        await delay(50);
        win.focus();
        win.print();
      } catch (error) {
        cleanup();
        reject(error instanceof Error ? error : new Error('No se pudo imprimir el reporte.'));
      }
    };

    iframe.addEventListener('load', on_load, { once: true });
    document.body.appendChild(iframe);
    iframe.srcdoc = html;
  });
}

function parseBigInt(value: string | null | undefined): bigint {
  if (!value || !/^-?\d+$/.test(value)) return 0n;
  try {
    return BigInt(value);
  } catch {
    return 0n;
  }
}

function parseIsoDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function compareSalesReportRowsForPrint(left: SalesReportRow, right: SalesReportRow): number {
  const left_meta = buildSalesReportPaymentSortMeta(left.payment_methods);
  const right_meta = buildSalesReportPaymentSortMeta(right.payment_methods);

  if (left_meta.priority !== right_meta.priority) {
    return left_meta.priority - right_meta.priority;
  }

  if (left_meta.sort_order !== right_meta.sort_order) {
    return left_meta.sort_order - right_meta.sort_order;
  }

  const paid_at_cmp = parseRowDate(left.paid_at) - parseRowDate(right.paid_at);
  if (paid_at_cmp !== 0) return paid_at_cmp;
  return left.sale_order_id.localeCompare(right.sale_order_id, 'en');
}

function buildSalesReportPaymentSortMeta(
  methods: readonly SalesReportPaymentMethod[],
): SalesReportPaymentSortMeta {
  const primary = selectPrimaryPrintablePaymentMethod(methods);
  if (!primary) {
    return {
      group: 'cash',
      priority: 0,
      sort_order: Number.MAX_SAFE_INTEGER,
      method_name: labelForPaymentGroup('cash'),
      section_key: 'cash',
      section_label: labelForPaymentGroup('cash'),
    };
  }

  if (primary.payment_method_code === 'cash') {
    return {
      group: 'cash',
      priority: 0,
      sort_order: primary.sort_order,
      method_name: primary.payment_method_name,
      section_key: 'cash',
      section_label: labelForPaymentGroup('cash'),
    };
  }
  if (primary.payment_method_code === 'debit_card') {
    return {
      group: 'debit_card',
      priority: 1,
      sort_order: primary.sort_order,
      method_name: primary.payment_method_name,
      section_key: 'debit_card',
      section_label: labelForPaymentGroup('debit_card'),
    };
  }
  if (primary.payment_method_code === 'credit_card') {
    return {
      group: 'credit_card',
      priority: 2,
      sort_order: primary.sort_order,
      method_name: primary.payment_method_name,
      section_key: 'credit_card',
      section_label: labelForPaymentGroup('credit_card'),
    };
  }

  return {
    group: 'cash',
    priority: 0,
    sort_order: primary.sort_order,
    method_name: labelForPaymentGroup('cash'),
    section_key: 'cash',
    section_label: labelForPaymentGroup('cash'),
  };
}

function labelForPaymentGroup(group: SalesReportPaymentGroup): string {
  if (group === 'cash') return 'Efectivo';
  if (group === 'debit_card') return 'Tarjeta de debito';
  if (group === 'credit_card') return 'Tarjeta de credito';
  return 'Efectivo';
}

function selectPrimaryPrintablePaymentMethod(
  methods: readonly SalesReportPaymentMethod[],
): SalesReportPaymentMethod | null {
  const printable_methods = methods.filter((method) =>
    isPrintablePaymentMethodCode(method.payment_method_code),
  );
  if (printable_methods.length === 0) return null;
  return (
    [...printable_methods].sort((left, right) => {
      if (left.sort_order !== right.sort_order) return left.sort_order - right.sort_order;
      return left.payment_method_name.localeCompare(right.payment_method_name, 'es-MX');
    })[0] ?? null
  );
}

function isPrintablePaymentMethodCode(code: string): boolean {
  return code === 'cash' || code === 'debit_card' || code === 'credit_card';
}

function parseRowDate(value: string): number {
  const time = Date.parse(value);
  return Number.isNaN(time) ? 0 : time;
}

function createSalesReportAmountMap(columns: readonly SalesReportColumn[]): Map<string, bigint> {
  const totals = new Map<string, bigint>();
  for (const column of columns) totals.set(column.column_key, 0n);
  return totals;
}

function renderSalesReportSectionTotalsRow(args: {
  meta: SalesReportPaymentSortMeta;
  visible_columns: readonly SalesReportColumn[];
  column_totals: ReadonlyMap<string, bigint>;
  section_total: bigint;
}): string {
  const totals_cells = args.visible_columns
    .map((column) => {
      const amount = args.column_totals.get(column.column_key) ?? 0n;
      return `<td class="amount amount--subtotal">${escapeHtml(formatCentsCompact(amount.toString()))}</td>`;
    })
    .join('');

  const section_name =
    args.meta.method_name === '\uffff' ? args.meta.section_label : args.meta.method_name;

  return `
    <tr class="subtotal-row">
      <td class="subtotal-label">
        Total por metodo
        <strong>${escapeHtml(section_name)}</strong>
      </td>
      ${totals_cells}
      <td class="amount amount--subtotal">${escapeHtml(formatCentsCompact(args.section_total.toString()))}</td>
    </tr>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
